import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { CheckCircle2, ArrowLeft, ArrowRight, CornerDownLeft } from "lucide-react";

function normalizeUrl(raw) {
  let s = String(raw || "").trim();
  if (!s) return "";
  if (!/^https?:\/\//i.test(s)) s = "https://" + s;
  try {
    const u = new URL(s);
    if (!/^([a-z0-9-]+\.)+[a-z]{2,}$/i.test(u.hostname)) return null;
    return u.href;
  } catch {
    return null;
  }
}

export default function Apply() {
  const [config, setConfig] = useState(null);
  const [responses, setResponses] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/public/form").then(({ data }) => setConfig(data)).catch(() => setError("Could not load the form."));
  }, []);

  const questions = useMemo(() => config?.questions || [], [config]);
  const total = questions.length;
  const q = questions[step];
  const setVal = (id, v) => setResponses((r) => ({ ...r, [id]: v }));

  const submit = useCallback(async (finalResponses) => {
    setError("");
    setSubmitting(true);
    try {
      const payload = { responses: questions.map((qq) => ({ id: qq.id, label: qq.label, answer: finalResponses[qq.id] ?? "" })) };
      const { data } = await api.post("/public/apply", payload);
      if (data.redirect_url) {
        window.location.href = data.redirect_url;
        return;
      }
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  }, [questions]);

  const advance = useCallback((valueOverride) => {
    if (!q) return;
    let value = valueOverride !== undefined ? valueOverride : responses[q.id];
    if (q.required && !String(value ?? "").trim()) {
      setError(`${q.label} is required`);
      return;
    }
    if (q.type === "url" && String(value ?? "").trim()) {
      const normalized = normalizeUrl(value);
      if (!normalized) {
        setError("Please enter a valid website URL (e.g. acme.com)");
        return;
      }
      value = normalized;
      setVal(q.id, normalized);
    }
    setError("");
    if (step < total - 1) {
      setStep((s) => s + 1);
    } else {
      submit({ ...responses, [q.id]: value });
    }
  }, [q, responses, step, total, submit]);

  const back = () => { setError(""); setStep((s) => Math.max(0, s - 1)); };

  const chooseOption = (opt) => {
    setVal(q.id, opt);
    setTimeout(() => advance(opt), 220);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && q?.type !== "long_text") {
      e.preventDefault();
      advance();
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b-2 border-white/15 focus:border-[#4690d9] rounded-none px-1 py-3 text-2xl text-white placeholder:text-slate-600 focus:outline-none transition-colors";

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <p className="eyebrow">{error || "Loading…"}</p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="glass-panel p-10 max-w-lg text-center fade-up" data-testid="apply-success">
          <CheckCircle2 size={40} className="text-emerald-400 mx-auto mb-4" />
          <h1 className="text-2xl font-extrabold text-white font-heading mb-2">Application received</h1>
          <p className="text-slate-300 text-sm">{config.success_message}</p>
        </div>
      </div>
    );
  }

  const pct = total ? Math.round(((step + 1) / total) * 100) : 0;

  return (
    <div className="min-h-screen flex flex-col" data-testid="apply-form">
      {/* progress bar */}
      <div className="fixed top-0 left-0 right-0 h-1.5 bg-white/5 z-50">
        <div className="h-full bg-gradient-to-r from-[#4690d9] to-[#9cc6f0] transition-all duration-500 ease-out" style={{ width: `${pct}%` }} data-testid="apply-progress" />
      </div>

      <div className="absolute top-6 left-0 right-0 text-center">
        <span className="text-lg font-extrabold tracking-tight font-heading">
          <span className="text-[#9cc6f0]">Growth</span><span className="text-white">OS</span>
        </span>
      </div>

      <div className="flex-1 flex items-center justify-center px-6">
        <div key={step} className="w-full max-w-2xl fade-up">
          <p className="eyebrow mb-4" data-testid="apply-step-indicator">
            Question {step + 1} of {total} · {pct}% complete
          </p>

          <label className="block text-2xl sm:text-3xl font-bold text-white font-heading mb-6 leading-snug">
            {q.label}{q.required && <span className="text-[#4690d9]"> *</span>}
          </label>

          <div data-testid={`apply-field-${q.id}`}>
            {q.type === "long_text" ? (
              <textarea
                autoFocus
                rows={4}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-4 py-3 text-lg text-white placeholder:text-slate-600 focus:outline-none focus:border-[#4690d9] resize-none transition-colors"
                value={responses[q.id] || ""}
                onChange={(e) => setVal(q.id, e.target.value)}
                placeholder="Type your answer…"
              />
            ) : q.type === "single_choice" ? (
              <div className="flex flex-col gap-2.5">
                {(q.options || []).map((opt) => (
                  <button
                    type="button"
                    key={opt}
                    data-testid={`apply-option-${q.id}-${opt}`}
                    onClick={() => chooseOption(opt)}
                    className={`text-left px-5 py-3.5 rounded-xl text-lg font-medium border transition-all duration-200 ${
                      responses[q.id] === opt
                        ? "bg-[#4690d9] border-[#5a9fe0] text-white"
                        : "bg-white/[0.03] border-white/12 text-slate-200 hover:border-[#4690d9]/60 hover:bg-white/[0.06]"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            ) : (
              <input
                autoFocus
                type={q.type === "email" ? "email" : q.type === "phone" ? "tel" : q.type === "url" ? "url" : "text"}
                className={inputCls}
                value={responses[q.id] || ""}
                onChange={(e) => setVal(q.id, e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={q.type === "url" ? "acme.com" : "Type your answer…"}
              />
            )}
          </div>

          {error && <p className="text-sm text-rose-400 mt-3" data-testid="apply-error">{error}</p>}

          <div className="flex items-center gap-3 mt-8">
            {step > 0 && (
              <button
                type="button"
                data-testid="apply-back-btn"
                onClick={back}
                className="flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-white/15 text-slate-300 hover:text-white text-sm font-semibold transition-colors"
              >
                <ArrowLeft size={15} /> Back
              </button>
            )}
            {q.type !== "single_choice" && (
              <button
                type="button"
                data-testid="apply-next-btn"
                onClick={() => advance()}
                disabled={submitting}
                className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#4690d9] hover:bg-[#5a9fe0] text-white font-semibold transition-all duration-300 hover:shadow-[0_0_24px_rgba(70,144,217,0.45)] disabled:opacity-50"
              >
                {submitting ? "Submitting…" : step < total - 1 ? "Next" : "Submit application"}
                {!submitting && (step < total - 1 ? <ArrowRight size={16} /> : <CheckCircle2 size={16} />)}
              </button>
            )}
            {q.type !== "long_text" && !submitting && (
              <span className="hidden sm:flex items-center gap-1 text-[11px] text-slate-500">
                press <CornerDownLeft size={11} /> Enter
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
